import {
  Avatar,
  Box,
  IconButton,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import Badge from "@mui/material/Badge";
import { Theme } from "@mui/material/styles";
import CircleIcon from "@mui/icons-material/Circle";
import NotificationsIcon from "@mui/icons-material/Notifications";
import createStyles from "@mui/styles/createStyles";
import makeStyles from "@mui/styles/makeStyles";
import { isEmpty } from "lodash";
import { useRouter } from "next/router";
import React from "react";
import {
  useFirestore,
  useFirestoreCollection,
  useSigninCheck,
  useUser,
} from "reactfire";
import {
  firestoreCollectionWithRefs,
  formatDateTime,
  imageSize,
  shallowTSToDates,
} from "@converge-collective/common/util";
import { WithRef } from "@converge-collective/common/models/Base";
import { Notification } from "@converge-collective/common/models/Notification";
import convergeLogo from "~/public/static/img/converge_logo_final.svg";
import useProfile from "~/hooks/useProfile";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      padding: theme.spacing(0.5),
      width: 65,
      zIndex: 2000,
    },
    notificationIcon: {
      fontSize: 40,
    },
    notificationItem: {
      whiteSpace: "normal",
    },
    unreadIcon: {
      fontSize: 10,
    },
  })
);

const NotificationItem = ({
  notification,
  onClick,
}: {
  notification: WithRef<Notification>;
  onClick: () => void;
}) => {
  const router = useRouter();
  const classes = useStyles();
  const handleClick = async () => {
    onClick();
    notification.ref.update({
      isRead: true,
    });
    console.log("handleClick", { notification });
    const { linkUrl } = notification;
    if (linkUrl) {
      if (linkUrl.startsWith("#")) {
        // set hash urls manually - nextjs router prevents correct handling of
        // hashes by the `useHash` hook
        location.hash = linkUrl;
      } else {
        router.push(linkUrl);
      }
    }
  };

  return (
    <MenuItem
      divider={true}
      className={classes.notificationItem}
      key={notification.id}
      onClick={handleClick}
      sx={{ maxHeight: "70%" }}
    >
      <Avatar
        src={
          notification.profile
            ? imageSize(notification.profile.photoURL, 600)
            : convergeLogo
        }
        sx={{ mr: 1 }}
      >
        {notification.profile && (
          <img
            alt={notification.profile.name}
            style={{ maxWidth: "100%", maxHeight: "100%" }}
            src={notification.profile.photoURL}
          />
        )}
      </Avatar>
      <Box sx={{ marginRight: "auto" }}>
        {notification.title && (
          <Typography variant="body1" sx={{ fontWeight: "bold" }}>
            {notification.title}
          </Typography>
        )}
        <Typography
          key={notification.id}
          dangerouslySetInnerHTML={{ __html: notification.bodyHtml }}
        />
        <Typography variant="caption">
          {formatDateTime(notification.date)}
        </Typography>
      </Box>
      <CircleIcon
        sx={{ visibility: notification.isRead ? "hidden" : "visible" }}
        className={classes.unreadIcon}
        color="secondary"
      />
    </MenuItem>
  );
};

function NotificationsLoggedIn(): React.ReactElement {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const { data: user } = useUser();
  const [profile, setProfile] = useProfile(user?.uid);
  const firestore = useFirestore();

  const { data: notificationsData } = useFirestoreCollection<Notification>(
    firestore
      .collection("profiles")
      .doc(user?.uid)
      .collection("notifications")
      .orderBy("date", "desc")
      .limit(50)
  );

  const notifications =
    firestoreCollectionWithRefs<Notification>(notificationsData);

  // const unreadCount = notifications?.filter((n) => !n.isRead).length || 0;

  // open the notifications menu on click
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    // toggle the menu
    setAnchorEl(anchorEl ? null : event.currentTarget);
    // when the user opens the menu, set hasUnreadNotifications to false
    setProfile({ hasUnreadNotifications: false });
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <div className={classes.root}>
        <IconButton
          aria-label="notifications for current user"
          edge={false}
          onClick={handleClick}
          color="primary"
          size="large"
        >
          <Badge
            variant="dot"
            overlap="circular"
            invisible={!profile?.hasUnreadNotifications}
            badgeContent=" "
            color="secondary"
          >
            <NotificationsIcon className={classes.notificationIcon} />
          </Badge>
        </IconButton>

        <Menu
          keepMounted
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          MenuListProps={{
            "aria-labelledby": "basic-button",
          }}
          PaperProps={{
            sx: {
              width: 400,
              maxHeight: "85%",
            },
          }}
          transformOrigin={{ horizontal: "right", vertical: "top" }}
          anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        >
          {isEmpty(notifications) && (
            <MenuItem
              sx={{ p: 2 }}
              className={classes.notificationItem}
              onClick={handleClose}
            >
              <Avatar sx={{ mr: 1, bgcolor: "whitesmoke" }}>🎊</Avatar>
              <Typography>{"You're all caught up!"}</Typography>
            </MenuItem>
          )}
          {notifications?.map((n) => (
            <NotificationItem
              onClick={handleClose}
              key={n.id}
              notification={shallowTSToDates(n)}
            />
          ))}
        </Menu>
      </div>
    </>
  );
}

export default function Notifications(): React.ReactElement {
  const { data: signInCheckResult } = useSigninCheck();
  if (signInCheckResult?.signedIn) {
    return <NotificationsLoggedIn />;
  } else {
    return <div />;
  }
}
